import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Flashbar } from '@amzn/awsui-components-react';
import { notificationApiConfig } from '../graphql/notification-api-config';
import { API } from 'aws-amplify';

const TranscriptionNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const { user } = useAuth();
  const shownNotificationIds = useRef(new Set());
  const subscriptionRef = useRef(null);
  const pollTimeoutRef = useRef(null);
  
  // Function to mark notification as read in backend
  const markNotificationAsRead = async (notificationId) => {
    try {
      const response = await fetch(notificationApiConfig.url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': notificationApiConfig.apiKey,
        },
        body: JSON.stringify({
          query: `
            mutation MarkNotificationAsRead($id: ID!) {
              markNotificationAsRead(id: $id) {
                id
                isRead
              }
            }
          `,
          variables: { id: notificationId }
        })
      });
      
      const result = await response.json();
      
      if (result.errors) {
        console.error('Error marking notification as read:', result.errors);
      } else {
        console.log('Notification marked as read:', result.data.markNotificationAsRead);
      }
    } catch (error) {
      console.error('Error calling markNotificationAsRead:', error);
    }
  };
  
  useEffect(() => {
    if (!user) return;
    
    const fullUserId = user.attributes?.sub ? `${user.attributes.sub}::${user.username}` : user.username;
    
    const setupAmplifySubscription = async () => {
      try {
        const subscriptionQuery = `
          subscription OnTranscriptionComplete($userId: String!) {
            onTranscriptionComplete(userId: $userId) {
              id
              userId
              status
              filename
              message
              errorDetails
              createdAt
            }
          }
        `;
        
        subscriptionRef.current = API.graphql({
          query: subscriptionQuery,
          variables: { userId: fullUserId },
          authMode: 'API_KEY',
          apiKey: notificationApiConfig.apiKey,
          endpoint: notificationApiConfig.url
        }).subscribe({
          next: (data) => {
            if (data && data.data && data.data.onTranscriptionComplete) {
              const notification = data.data.onTranscriptionComplete;
              
              if (!shownNotificationIds.current.has(notification.id)) {
                shownNotificationIds.current.add(notification.id);
                
                // Create content with error details for failed transcriptions
                let content = notification.message;
                if (notification.status !== 'SUCCESS' && notification.errorDetails) {
                  content += ` - ${notification.errorDetails}`;
                }
                
                const uiNotification = {
                  id: `realtime-${notification.id}`,
                  originalId: notification.id,
                  type: notification.status === 'SUCCESS' ? 'success' : 'error',
                  content: content,
                  dismissible: true,
                  onDismiss: async () => {
                    // Mark as read in backend
                    await markNotificationAsRead(notification.id);
                    // Remove from UI
                    setNotifications(prev => prev.filter(n => n.originalId !== notification.id));
                    
                    // Dispatch custom event to notify other components
                    window.dispatchEvent(new CustomEvent('notificationDismissed', {
                      detail: { notificationId: notification.id }
                    }));
                  }
                };
                
                setNotifications(prev => [...prev, uiNotification]);
                
                // Removed auto-dismiss - notifications persist until manually dismissed
              }
            }
          },
          error: (error) => {
            setupSmartPolling(fullUserId);
          }
        });
        
      } catch (error) {
        setupSmartPolling(fullUserId);
      }
    };
    
    const setupSmartPolling = (fullUserId) => {
      let pollInterval = 2000;
      const maxPollInterval = 8000;
      let consecutiveEmptyPolls = 0;
      
      const pollForNotifications = async () => {
        try {
          const response = await fetch(notificationApiConfig.url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'x-api-key': notificationApiConfig.apiKey,
            },
            body: JSON.stringify({
              query: `
                query GetNotificationsByUser($userId: String!) {
                  getNotificationsByUser(userId: $userId) {
                    id
                    userId
                    status
                    filename
                    message
                    errorDetails
                    createdAt
                  }
                }
              `,
              variables: { userId: fullUserId }
            })
          });
          
          const result = await response.json();
          
          if (result.data && result.data.getNotificationsByUser) {
            const backendNotifications = result.data.getNotificationsByUser;
            
            const newNotifications = backendNotifications.filter(
              notification => !shownNotificationIds.current.has(notification.id)
            );
            
            if (newNotifications.length > 0) {
              consecutiveEmptyPolls = 0;
              pollInterval = 2000;
              
              newNotifications.forEach(notification => {
                shownNotificationIds.current.add(notification.id);
                
                // Create content with error details for failed transcriptions
                let content = notification.message;
                if (notification.status !== 'SUCCESS' && notification.errorDetails) {
                  content += ` - ${notification.errorDetails}`;
                }
                
                const uiNotification = {
                  id: `polling-${notification.id}`,
                  originalId: notification.id,
                  type: notification.status === 'SUCCESS' ? 'success' : 'error',
                  content: content,
                  dismissible: true,
                  onDismiss: async () => {
                    // Mark as read in backend
                    await markNotificationAsRead(notification.id);
                    // Remove from UI
                    setNotifications(prev => prev.filter(n => n.originalId !== notification.id));
                    
                    // Dispatch custom event to notify other components
                    window.dispatchEvent(new CustomEvent('notificationDismissed', {
                      detail: { notificationId: notification.id }
                    }));
                  }
                };
                
                setNotifications(prev => [...prev, uiNotification]);
                
                // Removed auto-dismiss - notifications persist until manually dismissed
              });
            } else {
              consecutiveEmptyPolls++;
              if (consecutiveEmptyPolls > 3) {
                pollInterval = Math.min(pollInterval * 1.2, maxPollInterval);
              }
            }
          }
        } catch (error) {
          pollInterval = Math.min(pollInterval * 1.5, maxPollInterval);
        }
        
        pollTimeoutRef.current = setTimeout(pollForNotifications, pollInterval);
      };
      
      pollForNotifications();
    };
    
    setupAmplifySubscription();
    
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
      }
    };
  }, [user]);
  
  if (notifications.length === 0) {
    return null;
  }
  
  return <Flashbar items={notifications} />;
};

export default TranscriptionNotifications;
